import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  NavigationBar,
  NavigationList,
  NavigationItem,
  NavigationLink,
  NavigationSubmenu,
  NavigationSubmenuItem,
  NavigationRightMenuButton,
  NavigationRightMenu,
  NavigationCollapsedIcon,
  NavigationRightMenuSearch,
  SearchInput,
  NavigationRightMenuButtonLink,
} from "./NavigationStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSortDown,
  faBars,
  faTimes,
  faPhoneVolume,
} from "@fortawesome/free-solid-svg-icons";

function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Create refs for menu and submenus
  const menuRef = useRef(null);
  const submenuRefs = useRef({
    opdrachtgevers: null,
    zorgprofessionals: null,
  });
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const toggleSubmenu = (menuName) => {
    setOpenSubmenus((prev) => {
      // If the clicked menu is already open, close it by removing it from the list
      if (prev.includes(menuName)) {
        return prev.filter((name) => name !== menuName);
      }

      // Open the clicked submenu, without closing others
      return [...prev, menuName];
    });
  };

  // Focus search input when opening
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  /**
   * Handle the search input value change
   * @param {*} e
   */
  function handleSearchChange(e) {
    setSearchQuery(e.target.value);
  }

  /**
   * Handle the search submit
   * @param {*} e
   */
  function handleSearchSubmit(e) {
    e.preventDefault();
    if (searchQuery) {
      navigate(`/search?query=${searchQuery}`);
    }
  }

  // Handle click outside to close menu and submenus
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }

      if (!menuOpen) {
        // Close submenu if clicked outside of it (when menu is collapsed)
        if (
          submenuRefs.current["opdrachtgevers"] &&
          !submenuRefs.current["opdrachtgevers"].contains(event.target)
        ) {
          setOpenSubmenus((prev) =>
            prev.filter((submenuName) => submenuName !== "opdrachtgevers")
          );
        }
        if (
          submenuRefs.current["zorgprofessionals"] &&
          !submenuRefs.current["zorgprofessionals"].contains(event.target)
        ) {
          setOpenSubmenus((prev) =>
            prev.filter((submenuName) => submenuName !== "zorgprofessionals")
          );
        }
      }
    }

    if (menuOpen || openSubmenus.length > 0) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, openSubmenus]);

  return (
    <NavigationBar ref={menuRef}>
      {/* Logo */}
      <Link to="/">
        <img
          src="/assets/logo.jpg"
          alt="Logo"
          style={{ height: "20px", marginTop: "5px" }}
        />
      </Link>

      {/* Navigation Links */}
      <NavigationList
        $menuOpen={menuOpen}
        $searchOpen={searchOpen}
        className={menuOpen ? "active" : ""}
      >
        <NavigationItem>
          <Link
            to="/about"
            onClick={() => {
              setMenuOpen(false);
              setOpenSubmenus([]);
            }}
          >
            <NavigationLink>Over Smart</NavigationLink>
          </Link>
        </NavigationItem>

        {/* Commissioners page */}
        <NavigationItem $menuOpen={menuOpen} className="dropdown">
          <NavigationLink onClick={() => toggleSubmenu("opdrachtgevers")}>
            <span>Opdrachtgevers</span>
            <FontAwesomeIcon
              icon={faSortDown}
              style={{ paddingLeft: "10px" }}
            />
          </NavigationLink>
          <NavigationSubmenu
            ref={(el) => (submenuRefs.current["opdrachtgevers"] = el)}
            style={{
              display: openSubmenus.includes("opdrachtgevers")
                ? "block"
                : "none",
            }}
            $menuOpen={menuOpen}
            onClick={() => setMenuOpen(false)}
          >
            <NavigationSubmenuItem>
              <Link
                to="/commissioners/flex"
                onClick={() => setOpenSubmenus([])}
              >
                Smart Flex
              </Link>
            </NavigationSubmenuItem>
            <NavigationSubmenuItem>
              <Link
                to="/commissioners/crisis"
                onClick={() => setOpenSubmenus([])}
              >
                Smart Crisis Team
              </Link>
            </NavigationSubmenuItem>
            <NavigationSubmenuItem>
              <Link
                to="/commissioners/planning"
                onClick={() => setOpenSubmenus([])}
              >
                Smart Planning
              </Link>
            </NavigationSubmenuItem>
          </NavigationSubmenu>
        </NavigationItem>

        {/* Healthcare Professionals page */}
        <NavigationItem $menuOpen={menuOpen} className="dropdown">
          <NavigationLink onClick={() => toggleSubmenu("zorgprofessionals")}>
            <span>Zorgprofessionals</span>
            <FontAwesomeIcon
              icon={faSortDown}
              style={{ paddingLeft: "10px" }}
            />
          </NavigationLink>
          <NavigationSubmenu
            ref={(el) => (submenuRefs.current["zorgprofessionals"] = el)}
            style={{
              display: openSubmenus.includes("zorgprofessionals")
                ? "block"
                : "none",
            }}
            $menuOpen={menuOpen}
            onClick={() => setMenuOpen(false)}
          >
            <NavigationSubmenuItem>
              <Link to="/healthcare/work" onClick={() => setOpenSubmenus([])}>
                Direct aanmelden
              </Link>
            </NavigationSubmenuItem>
            <NavigationSubmenuItem>
              <Link
                to="/healthcare/vacancy"
                onClick={() => setOpenSubmenus([])}
              >
                Vacatures
              </Link>
            </NavigationSubmenuItem>
          </NavigationSubmenu>
        </NavigationItem>

        <NavigationItem>
          <Link
            to="https://mijn.smart-personeel.nl/"
            target="_blank"
            onClick={() => setMenuOpen(false)}
          >
            <NavigationLink>Mijn Smart</NavigationLink>
          </Link>
        </NavigationItem>

        <NavigationItem>
          <Link to="/contact" onClick={() => setMenuOpen(false)}>
            <NavigationLink>Contact</NavigationLink>
          </Link>
        </NavigationItem>
      </NavigationList>

      {/* Right Menu: Search and Appointment Button */}
      <NavigationRightMenu>
        {/* Hamburger Icon */}
        <NavigationCollapsedIcon
          className={searchOpen ? "active" : ""}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </NavigationCollapsedIcon>

        {/* Search Icon */}
        <NavigationRightMenuSearch>
          <form onSubmit={handleSearchSubmit}>
            <SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Zoeken..."
              value={searchQuery}
              onChange={handleSearchChange}
              $searchOpen={searchOpen}
            />
            <FontAwesomeIcon
              onClick={() => setSearchOpen(!searchOpen)}
              icon={faSearch}
              style={{ cursor: "pointer", fontSize: "20px" }}
            />
          </form>
        </NavigationRightMenuSearch>

        {/* Appointment Button */}
        <NavigationRightMenuButtonLink as={Link} to="tel:+31850607774">
          <NavigationRightMenuButton>
            {" "}
            <FontAwesomeIcon
              icon={faPhoneVolume}
              style={{ color: "white", marginRight: "10px" }}
            />
            +31850607774
          </NavigationRightMenuButton>
        </NavigationRightMenuButtonLink>
      </NavigationRightMenu>
    </NavigationBar>
  );
}

export default Navigation;
